import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageLayout } from "@shared/layouts/PageLayout";
import { adminDashboardBreadcrumbs } from "@features/AdminDashboard/admin-dashboard-breadcrumbs";
import { useListSubscriptions } from "@shared/generated/radar/queries/subscriptionAuditController/useListSubscriptions";
import type { ListSubscriptionsQueryResponse } from "@shared/generated/radar/models/subscriptionAuditController/ListSubscriptions";
import {
  SubscriptionsAuditFilters,
  type EnabledFilter,
  type SubscriptionsAuditFilterValues,
} from "@features/EventSubscriptionsAudit/SubscriptionsAuditFilters";
import { SubscriptionsAuditList } from "@features/EventSubscriptionsAudit/SubscriptionsAuditList";

const PAGE_SIZE = 25;

const breadcrumbs = {
  current: adminDashboardBreadcrumbs.eventSubscriptions,
  previous: [adminDashboardBreadcrumbs.dashboard],
};

type Subscription = ListSubscriptionsQueryResponse["items"][number];

const ENABLED_FILTER_VALUES: EnabledFilter[] = ["all", "enabled", "disabled"];

export function EventSubscriptionsAuditPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const parsedPage = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const userId = searchParams.get("userId") ?? "";
  const enabledParam = searchParams.get("enabled");
  const enabledFilter: EnabledFilter = ENABLED_FILTER_VALUES.includes(
    enabledParam as EnabledFilter,
  )
    ? (enabledParam as EnabledFilter)
    : "all";
  const filters: SubscriptionsAuditFilterValues = { userId, enabledFilter };

  const query = useListSubscriptions({
    page,
    pageSize: PAGE_SIZE,
    userId: userId.trim() || undefined,
    enabled: enabledFilter === "all" ? undefined : enabledFilter === "enabled",
  });

  const subscriptions = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // If a filter change or an external nav shrinks the result set below the
  // current page, snap back to the last valid page instead of showing empty.
  useEffect(() => {
    if (query.isLoading) return;
    if (totalPages <= 0) return;
    if (page <= totalPages) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(totalPages));
        return next;
      },
      { replace: true },
    );
  }, [query.isLoading, page, setSearchParams, totalPages]);

  const handleFiltersChange = (values: SubscriptionsAuditFilterValues) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (values.userId) next.set("userId", values.userId);
        else next.delete("userId");
        if (values.enabledFilter !== "all")
          next.set("enabled", values.enabledFilter);
        else next.delete("enabled");
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      return next;
    });
  };

  const handleViewDeliveries = (subscription: Subscription) => {
    navigate(`/admin/event-subscriptions/${subscription.id}/deliveries`, {
      state: {
        subscriptionName: subscription.name,
        targetUrl: subscription.targetUrl,
        userName: subscription.userName,
      },
    });
  };

  return (
    <PageLayout breadcrumbs={breadcrumbs}>
      <SubscriptionsAuditFilters
        values={filters}
        onChange={handleFiltersChange}
      />
      <SubscriptionsAuditList
        subscriptions={subscriptions}
        isLoading={query.isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={handlePageChange}
        onViewDeliveries={handleViewDeliveries}
      />
    </PageLayout>
  );
}
